import * as Sentry from '@sentry/browser'
import { BrowserOptions, Scope } from '@sentry/browser'
import { ConnotativeError } from './E'
import { SimpleObj, Concat, Args } from './types'

// 这个类里最好不要有和 DOM 相关的依赖，可以都从 constructor
// 传进来，这样子便于测试
export class ErrorCollector {
	Sentry = Sentry

	call: ErrorCollectorCall

	private inited = false

	constructor(
		prelude?: ErrorCollectorPrelude | ErrorCollector,
		private _options: ErrorCollectorOptions = {},
	) {
		if (prelude instanceof ErrorCollector) return prelude

		this.call = this._createCallFn(prelude)

		this._ensure(() => {
			for (const call of this.call.calls) {
				if (call[0] === 'init') {
					this.init(call[1])
					break
				}
			}
		})
	}

	/**
	 * https://docs.sentry.io/error-reporting/configuration/?platform=browser
	 *
	 * 当 config.debug 为 true 时，不会发送错误到 Sentry ，转而使用
	 * console.error 打印错误信息
	 */
	init(options: ErrorCollectorInitOptions = {}) {
		this._ensure(() => {
			if (options.Sentry) {
				this.Sentry = options.Sentry
			}

			const _options = { ...options }
			delete _options.Sentry

			this.Sentry.init({
				..._options,
				beforeSend: this._wrapBeforeSend(_options),
			})
			this.inited = true
			if (typeof this._options.onInited === 'function') {
				this._options.onInited()
			}

			this._callScheduled()
		})
	}

	/**
	 * https://docs.sentry.io/error-reporting/capturing/?platform=browser
	 *
	 * 参数是一个对象，其中除了 message 属性以外，其他的键值对会作为
	 * Extra 传到 Sentry
	 */
	message(info: { message: string } & SimpleObj<any>) {
		// eslint-disable-next-line no-shadow
		const { Sentry } = this

		this._ensure(() => {
			Sentry.withScope(sendMessage)
		})

		function sendMessage(scope: Scope) {
			Object.keys(info).forEach(k => {
				if (k === 'message') return
				scope.setExtra(k, info[k])
			})

			Sentry.captureMessage(info.message)
		}
	}

	/**
	 * https://docs.sentry.io/error-reporting/capturing/?platform=browser
	 *
	 * 参数是 Error 的实例，如果这个参数有一个叫 detail 的属性
	 *
	 * * 如果 detail 是一个 Error 的实例，那么 detail 会替代原参数
	 * * 如果 detail 是一个对象，那么会作为 Extra 传到 Sentry
	 * * 如果是其他情况，那么会作为名为 detail 的 Extra 传到 Sentry
	 */
	error(error: Error | ConnotativeError<any>) {
		// eslint-disable-next-line no-shadow
		const { Sentry } = this

		this._ensure(() => {
			Sentry.withScope(sendError)
		})

		function sendError(scope: Scope) {
			let detail: any = null

			if ('detail' in error) {
				if (error.detail instanceof Error) {
					error = error.detail
				} else {
					detail = error.detail
				}
			}

			if (detail && typeof detail === 'object') {
				Object.keys(detail).forEach(k => {
					scope.setExtra(k, detail[k])
				})
			} else {
				scope.setExtra('detail', detail)
			}

			Sentry.captureException(error)
		}
	}

	/**
	 * https://docs.sentry.io/enriching-error-data/context/?platform=browser
	 *
	 * 设置当前的上下文，这些信息会和接下来上报的所有 message/error 关联
	 */
	context(info: {
		user?: Sentry.User
		tags?: SimpleObj<string>
		level?: Sentry.Severity
		fingerprint?: string[]
		extra?: SimpleObj<any>
	}) {
		// eslint-disable-next-line no-shadow
		const { Sentry } = this

		this._ensure(() => {
			Sentry.configureScope(setContext)
		})

		function setContext(scope: Scope) {
			info.user && scope.setUser(info.user)
			info.level && scope.setLevel(info.level)
			info.fingerprint && scope.setFingerprint(info.fingerprint)

			if (info.tags) {
				Object.keys(info.tags).forEach(k => scope.setTag(k, info.tags![k]))
			}

			if (info.extra) {
				Object.keys(info.extra).forEach(k => scope.setExtra(k, info.extra![k]))
			}
		}
	}

	/**
	 * https://docs.sentry.io/enriching-error-data/breadcrumbs/?platform=browser
	 *
	 * 记录用户行为，会与接下来上报所有的 message/error 关联
	 */
	breadcrumb(info: Sentry.Breadcrumb) {
		this._ensure(() => {
			this.Sentry.configureScope(scope => {
				scope.addBreadcrumb(info)
			})
		})
	}

	private _calledScheduled = false
	private _callScheduled() {
		if (this._calledScheduled) return
		if (!this.inited) return
		this._calledScheduled = true

		let encountedInitCall = false
		this.call.calls.forEach((call: ErrorCollectorPreludeCall) => {
			// 第一个 init 调用一定会被提前处理，所以要跳过
			if (call[0] === 'init' && !encountedInitCall) {
				encountedInitCall = true
			} else {
				;(this as any).call(...call)
			}
		})
	}

	/**
	 * 用于在实例化后替代掉原来用于存储调用信息的数组（global.uec）
	 * 第一个参数是 ErrorCollector 的方法名，第二个参数是对应方法的参数数组
	 */
	private _call<FN extends ErrorCollectorMethod>(
		fnName: FN,
		...args: ErrorCollectorMethodArgs[FN]
	) {
		if (this.inited) {
			;(this as any)[fnName](...args)
		} else if (fnName === 'init') {
			this.init(...(args as any))
		} else {
			this.call.calls.push([fnName, ...args] as any)
		}
	}

	private _createCallFn(prelude?: ErrorCollectorPrelude) {
		const call = this._call.bind(this)

		call.collector = this

		if (prelude && typeof prelude === 'function') {
			call.calls = prelude.calls || []
		} else {
			call.calls = []
		}

		return call
	}

	private _wrapBeforeSend(
		config: BrowserOptions,
	): NonNullable<BrowserOptions['beforeSend']> {
		return (event, hint, ...args) => {
			if (config.debug) {
				if (hint) {
					console.error(hint.originalException)
				} else if (event) {
					console.error(event.message, event)
				}
			}

			if (typeof config.beforeSend === 'function') {
				return config.beforeSend(event, hint, ...args)
			} else {
				return config.debug ? null : event
			}
		}
	}

	private _ensure(fn: () => void) {
		try {
			fn()
		} catch (err) {
			console.error(err)
		}
	}
}

export interface ErrorCollectorOptions {
	onInited?: () => void
}

export type ErrorCollectorInitOptions = BrowserOptions & {
	Sentry?: typeof Sentry
}

export type ErrorCollectorMethod = Exclude<
	keyof ErrorCollector,
	'call' | 'Sentry'
>

export type ErrorCollectorMethodArgs = {
	[K in ErrorCollectorMethod]: Args<ErrorCollector[K]>
}

export type ErrorCollectorPreludeCallMap = {
	[K in ErrorCollectorMethod]: Concat<K, ErrorCollectorMethodArgs[K]>
}

export type ErrorCollectorPreludeCall = ErrorCollectorPreludeCallMap[ErrorCollectorMethod]

export interface ErrorCollectorPrelude {
	<FN extends ErrorCollectorMethod>(
		fnName: FN,
		...args: ErrorCollectorMethodArgs[FN]
	): void
	calls?: ErrorCollectorPreludeCall[]
}

export interface ErrorCollectorCall {
	<FN extends ErrorCollectorMethod>(
		fnName: FN,
		...args: ErrorCollectorMethodArgs[FN]
	): void
	calls: ErrorCollectorPreludeCall[]
	collector?: ErrorCollector
}
